import React, { Suspense } from 'react'
import ChatBotUI from './ChatBotUI'
import CaseLevelHeader from 'components/features/dashboard/CaseLevelHeader'
import './styles.scss'
import { ChatBotProps, defaultChatBotProps } from './types'
import { useAppSelector } from 'hooks/store'

/** Minimal error boundary to avoid taking down the host app */
class ChatBotErrorBoundary extends React.Component<
  { onError?: (error: Error) => void; children: React.ReactNode },
  { hasError: boolean; error?: Error }
> {
  constructor(props: {
    onError?: (error: Error) => void
    children: React.ReactNode
  }) {
    super(props)
    this.state = { hasError: false, error: undefined }
  }
  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error }
  }
  componentDidCatch(error: Error) {
    this.props.onError?.(error)
  }
  render() {
    if (this.state.hasError) {
      return (
        <section
          role='status'
          aria-live='polite'
          className='ChatBot ChatBot--error'
        >
          <div className='message'>
            <p>
              <strong>Chat service temporarily unavailable.</strong>
            </p>
            <p>Please refresh the page to try again.</p>
            {this.state.error && (
              <details style={{ marginTop: '1rem', fontSize: '0.875rem' }}>
                <summary>Technical details</summary>
                <pre style={{ whiteSpace: 'pre-wrap', marginTop: '0.5rem' }}>
                  {this.state.error.message}
                </pre>
              </details>
            )}
          </div>
        </section>
      )
    }
    return this.props.children
  }
}

const ChatBot = (props: ChatBotProps = defaultChatBotProps): JSX.Element => {
  const activeCase = useAppSelector((state) => state.activeCase)
  return (
    <>
      <CaseLevelHeader
        text={activeCase.matter?.firm.name}
        status={
          activeCase.matter?.dashBoardHeaderDescription
            ? activeCase.matter?.dashBoardHeaderDescription
            : ''
        }
        iconUrl={activeCase.matter?.firm.iconUrl}
      />
      <ChatBotErrorBoundary onError={props.onError}>
        <Suspense fallback={<div>Loading chat...</div>}>
          <ChatBotUI {...props} />
        </Suspense>
      </ChatBotErrorBoundary>
    </>
  )
}

export default ChatBot

// Re-exports
export type { ChatBotProps, ChatMessage, CaseId, RouteParams } from './types'
export { defaultChatBotProps, MessageType, MessageStatus } from './types'
export { TIMING, ERROR_MESSAGES, UI } from './constants'
